<script setup lang="ts">
import {computed, nextTick, reactive, ref, useTemplateRef, watch} from "vue";
import {useRouter} from "vue-router";
import {useStorage, watchDebounced} from "@vueuse/core";
import {useToast} from "primevue/usetoast";
import {getStimuli as getStimuliFromApi} from "@/Services/stimuli";
import {getQuestion, storeQuestion, updateQuestion} from "@/Services/questions";
import {ResetButton} from "@/Components/Forms";
import {QuestionTypes, StimulusTypes} from "@/Enums";
import {PageTitle, StockImageSearch, TagSelect, WriteWithAi} from "@/Components/Global";
import {getting} from "@/Composables/api";
import {
    Button,
    Checkbox,
    FileUpload,
    Icon,
    InputText,
    Label,
    Message,
    Popover,
    RadioButton,
    Select,
    SelectButton,
    Textarea,
    ToggleSwitch,
} from "@/Components/Library";
import StimulusSelect from "./StimulusSelect.vue";
import User from "../../../../Models/User";
import Question from "@/Models/Question";
import LaunchHeader from "../Launch/LaunchHeader.vue";
import StimulusDisplay from "@/Pages/User/Stimuli/StimulusDisplay.vue";
import {useMainStore} from "@/Stores/store";
import CoverImage from "../../Feed/CoverImage.vue";
import {getGroup} from "@/Services/groups.ts";
import {saveGroupQuestions} from "@/Services/group-questions.ts";

const router = useRouter();
const toast = useToast();
const $store = useMainStore();
const groupId = computed(() => router.currentRoute.value.params.group || router.currentRoute.value.query.group);

const defaultQuestion = {
    id: null,
    title: "",
    type: null,
    stimulus_id: 0,
    is_hidden: false,
    params: {
        image_options: false,
        allow_multiple: false,
        allow_comments: false,
        allow_voice_comments: false,
        allow_voice_responses: false,
        allow_annotations: false,
        randomize_options: false,
        show_charts: false,
        show_responses: false,
        allow_voting: false,
        limit_responses: false,
        response_limit: null,
        slider_range: false,
        disable_unresponded_comments: false,
        cover_image_type: "none" as "stimulus" | "custom" | "none",
        gatekeeper_all: false,
        matrix: false,
    },
    categories: [] as string[],
    options: [
        {
            value: "",
            temp_id: crypto.randomUUID(),
            id: 0,
            params: {gatekeeper_positive: false, reveal: false, reveal_tags: [], flagged: false},
        },
    ],
    tags: [],
};
const stockImageSearchEl = useTemplateRef("stockImageSearchEl");
const stockImage = ref();
const coverImageOptions = [
    {name: "No image", value: "none"},
    {name: "From stimulus", value: "stimulus"},
    {name: "Choose my own", value: "custom"},
];
const clearStockImage = () => {
    stockImage.value = null;
    stockImageSearchEl.value.clearSearch();
};

const $user = new User();
const question = useStorage(`U${$user.id}:questionForm`, defaultQuestion);
const showParams = useStorage(`U${$user.id}:questionForm:showParams`, false);

const allowedQuestionTypes = computed(() =>
    Object.values(QuestionTypes).filter((type) => {
        if (type.slug !== "post") {
            if (groupId.value) {
                return $store.group.can_create;
            }
            return $user.can("ask detailed questions") || $user.can("reuse questions");
        }
        if (type.slug === "annotation" && !$user.can("ask annotation questions")) {
            return false;
        }
        return true;
    }),
);
if (allowedQuestionTypes.value.length === 1) {
    defaultQuestion.type = allowedQuestionTypes.value[0].slug;
    question.value.type = allowedQuestionTypes.value[0].slug;
}

const search = reactive({
    groups: "",
    questions: "",
    stimuli: {
        search: "",
        type: "",
    },
});

const stimulusFilters = reactive({
    search: "",
    type: [],
    ownership: "all" as "all" | "mine",
    user: $store.group.is_admin ? $store.group?.user?.id : $user.id,
});
const coverImageUploadRef = useTemplateRef<FileUpload>("coverImageUploadRef");

const stimuli = ref([]);
const getStimuli = async () =>
    await getStimuliFromApi({
        filter: stimulusFilters,
        per_page: -1,
    }).then(({data}) => (stimuli.value = data));
//data
if (router.currentRoute.value.params.question) {
    Object.assign(question.value, defaultQuestion);
    getQuestion(router.currentRoute.value.params.question).then(async ({data}) => {
        console.log(data);
        data.categories = data.categories.map((category) => category.name);
        Object.assign(question.value, data);
        console.log("DDD", question.value);
        question.value.options = (data.options ?? []).map((option) => ({
            ...option,
            params: {
                gatekeeper_positive: false,
                reveal: false,
                ...(option.params ?? {}),
                reveal_tags: (option.params?.reveal_tags ?? []).map((tag) =>
                    typeof tag === "string" ? {name: tag} : tag,
                ),
            },
        }));
        if (data.type.slug === "text" && data.params.allow_annotations) {
            question.value.type = "annotation";
        } else {
            question.value.type = question.value.type.slug;
        }
        question.value.stimulus_id = router.currentRoute.value.query.stimulus ?? question.value.stimuli?.[0]?.id ?? 0;
        if (question.value.stimuli?.[0]) {
            stimuli.value.push(question.value.stimuli[0]);
        }
        await nextTick();
        question.value.params.cover_image_type = disabledCoverImageOptions({
            value: question.value.params.cover_image_type,
        })
            ? "none"
            : question.value.params.cover_image_type;
        console.log(question.value.params.cover_image_type);
    });
} else {
    if (question.value.id) {
        question.value = structuredClone(defaultQuestion);
    }
}

const selectedStimulus = computed(() => stimuli.value.find((S) => S.id === question.value?.stimulus_id));

const filterStimulusPanel = useTemplateRef("filterStimulusPanel");

watch(() => [stimulusFilters.type, stimulusFilters.ownership], getStimuli);

const $questionModel = computed(() => new Question(question.value));

const hasOptions = computed(() => $questionModel.value.is(["multiple_choice", "ranking", "weighting", "gatekeeper"]));

question.value.stock_image = null; // Resets stock image on reload

const optionInputRefs = ref<HTMLInputElement[]>([]);
const addOption = async (e) => {
    e.preventDefault();
    question.value.options.push({
        value: "",
        temp_id: crypto.randomUUID(),
        params: {gatekeeper_positive: false, reveal: false, reveal_tags: [], flagged: false},
    });
    await nextTick();
    const lastInput = optionInputRefs.value[optionInputRefs.value.length - 1];
    if (lastInput) {
        lastInput.$el?.focus();
    }
};

const removeOption = (i) => {
    console.log(i);
    if (question.value.options.length === 1) {
        question.value.options[0] = {
            value: "",
            id: 0,
            temp_id: crypto.randomUUID(),
            params: {gatekeeper_positive: false, reveal: false, reveal_tags: [], flagged: false},
        };
        return;
    }
    question.value.options.splice(i, 1);
};

const addMatrixCategory = (e) => {
    e.preventDefault();
    question.value.categories.push("");
};

const removeMatrixCategory = (i) => {
    if (question.value.categories.length === 1) {
        question.value.categories[0] = "";
        return;
    }
    question.value.categories.splice(i, 1);
};

const validate = () => {
    let validationMessage = null;

    if (!question.value.type) {
        validationMessage = "Please select a question type";
    }
    if (!question.value.title) {
        validationMessage = "Please enter a question";
    }
    if (question.value.type === "annotation" && !question.value.stimulus_id) {
        validationMessage = "Please select a stimulus";
    }
    if (
        ["multiple_choice", "ranking", "weighting", "gatekeeper"].indexOf(question.value.type) > -1 &&
        question.value.options.length < 2
    ) {
        validationMessage = "Please add at least two options";
    }
    if (
        question.value.type === "multiple_choice" &&
        question.value.params.matrix &&
        (question.value.categories ?? []).filter((category) => category.trim()).length < 2
    ) {
        validationMessage = "Please add at least two categories";
    }
    if (router.currentRoute.value.query.group && question.value.type !== "post") {
        const group = $store.group;
        if (group && !group.can_admin && !group.is_creator) {
            validationMessage = "Only creators can post detailed questions to this group";
        }
    }

    if (validationMessage) {
        toast.add({
            severity: "warn",
            summary: "Missing required field",
            detail: validationMessage,
            life: 3000,
        });
        return false;
    }

    return true;
};

const preview = async () => {
    const previewId = question.value.id;
    resetForm();
    await router.push({
        name: "questions.preview",
        params: {question: previewId},
        query: {
            ...router.currentRoute.value.query,
            from: "form",
            group: router.currentRoute.value.query.group,
        },
    });
};

const transformQuestion = () => {
    let {id, title, type, stimulus_id, is_hidden, params, options, categories} = question.value;

    if (type === "annotation") {
        type = "text";
        params = {...params, allow_annotations: true};
    }

    return {
        id,
        title,
        type,
        stimulus_id: stimulus_id || null,
        is_hidden,
        params,
        options: options?.map((option) => ({
            ...option,
            params: {
                ...(option.params ?? {}),
                reveal_tags: (option.params?.reveal_tags ?? [])
                    .map((tag) => (typeof tag === "string" ? tag : tag?.name))
                    .filter((tag): tag is string => !!tag),
            },
        })),
        categories,
        stock_image: stockImage.value?.id,
        tags: (question.value.tags ?? [])
            .map((tag) => (typeof tag === "string" ? tag : tag?.name))
            .filter((tag): tag is string => !!tag),
    };
};

const saving = ref<boolean | string>(false);
const save = async (e) => {
    try {
        console.log(question.value);
        // return;
        if (!validate()) {
            return;
        }
        saving.value = "Saving question";
        const postData = transformQuestion();
        const isEditing = !!question.value.id;
        const {data} = isEditing ? await updateQuestion(postData) : await storeQuestion(postData);

        console.log(data, fileRefs.value);

        data.type = data.type.slug;
        Object.assign(question.value, data);
        console.log(data, question.value);
        await nextTick();

        if (
            question.value.params.cover_image_type === "custom" &&
            !postData.stock_image &&
            coverImageUploadRef.value?.files.length
        ) {
            saving.value = "Uploading cover image";
            const files = await coverImageUploadRef.value.startUpload();
            console.log(files);
        }

        if (question.value.options?.length && question.value.params.image_options) {
            //TODO check/set parent id based on temp id
            console.log("uploading image options", fileRefs.value);
            saving.value = "Uploading option images";
            for (const fileRef of fileRefs.value) {
                console.log("fileRef", fileRef);
                await fileRef.startUpload();
            }
        }

        $store.feed.questions = [];
        $store.group.questions = [];
        if (!groupId.value) {
            await router.push({
                name: "questions.index",
                query: router.currentRoute.value.query,
            });
            useStorage("questions.index.sort", "title").value = "-updated_at";
        } else {
            if (!isEditing) {
                await saveGroupQuestions(groupId.value, {}, {questions: [question.value.id]});
            }
            await router.push({name: "feed", params: {group: groupId.value}});
        }
        resetForm();
    } catch (e) {
        console.log(e);
    } finally {
        saving.value = false;
    }
};

const fileRefs = useTemplateRef("optionUpload");

watch(stockImage, (nVal) => {
    if (nVal) {
        coverImageUploadRef.value.clearFiles();
    }
});

watch(
    () => question.value.stimulus_id,
    (nVal) => {
        if (!question.value.id) {
            if (nVal) {
                if (!coverImageUploadRef.value?.files.length && !stockImage.value) {
                    question.value.params.cover_image_type = "stimulus";
                }
            } else if (!coverImageUploadRef.value?.files.length && !stockImage.value) {
                question.value.params.cover_image_type = "none";
            }
        }
    },
);

watch(
    () => question.value?.type,
    (newType, oldType) => {
        if (newType !== "multiple_choice") {
            question.value.params.matrix = false;
        }

        if (newType === "annotation") {
            question.value.params.allow_multiple = true;
        } else {
            if (!question.value.id) {
                // question.value.params.allow_voice_responses = false;
                // question.value.params.allow_multiple = false;
            }
        }

        if (newType === "annotation") {
            showParams.value = true;
            // question.value.params.allow_comments = false;
            // question.value.params.allow_voice_comments = false;
            // question.value.params.allow_voice_responses = false;
            stimulusFilters.type = [
                "image",
                // "document",
                "slides",
            ];
            if (oldType && oldType !== "annotation") {
                question.value.stimulus_id = null;
            }
        } else if (newType === "gatekeeper") {
            showParams.value = true;
            if (question.value.params.gatekeeper_all === undefined) {
                question.value.params.gatekeeper_all = false;
            }
        } else if (oldType === "annotation") {
            // Only reset these when switching FROM annotation mode
            stimulusFilters.type = [];
            if (question.value.stimulus_id === null) {
                question.value.stimulus_id = 0;
            }
        }

        if (groupId.value && (!$store.group?.id || $store.group.id !== groupId.value)) {
            getGroup(router.currentRoute.value.query.group).then(({data}) => {
                $store.group = data;
                stimulusFilters.user = $store.group.is_admin ? $store.group?.user?.id : $user.id;
                stimulusFilters.ownership = $store.group.is_admin ? "all" : "mine";
                getStimuli();
            });
        } else {
            getStimuli();
        }

        if (hasOptions.value && !question.value?.options?.length) {
            question.value.options = [{value: "", id: 0}];
        }
    },
    {immediate: true},
);

watch(
    () => question.value.params.matrix,
    (matrix) => {
        if (matrix) {
            if (!question.value.categories?.length) {
                question.value.categories = ["", ""];
            }
        }
    },
);

watchDebounced(() => stimulusFilters.search, getStimuli, {debounce: 1000});

const resetForm = () => {
    stockImage.value = null;
    stockImageSearchEl.value?.clearSearch();
    search.stimuli.search = "";
    showParams.value = false;
    question.value = structuredClone(defaultQuestion);
    window.scrollTo({top: 0, behavior: "smooth"});
};

const disabledCoverImageOptions = (e) => {
    return (
        e.value === "stimulus" &&
        (!question.value.stimulus_id || ["audio", "document"].includes(selectedStimulus.value?.type.slug))
    );
};
watch(
    () => question.value.params.allow_voting,
    async (allow_voting) => {
        console.log(allow_voting, question.value.params.show_responses);
        if (allow_voting) {
            question.value.params.show_responses = true;
        }
    },
);

watch(
    () => question.value.params.show_responses,
    async (show_responses) => {
        console.log(show_responses, question.value.params.allow_voting);
        if (!show_responses) {
            question.value.params.allow_voting = false;
        }
    },
);

const canReuseStimulus = computed(() => {
    if (groupId.value) {
        return $store.group.can_admin;
    }
    return $user.can("reuse questions");
});
</script>
<template>
    <div class="m-container mx-auto max-w-5xl">
        <div class="mb-4" v-if="router.currentRoute.value.query.launch && $user.can('reuse questions')">
            <LaunchHeader @cancel="router.push({name: 'questions.index'})">
                <template #header>
                    <div class="flex grow justify-end">
                        <RouterLink :to="{name: 'questions.index', query: router.currentRoute.value.query}">
                            <Button
                                class="border bg-white !py-1 !pl-2 !pr-3 text-xs text-stone-700 shadow-sm hover:bg-stone-100"
                            >
                                Or reuse an existing question
                            </Button>
                        </RouterLink>
                    </div>
                </template>
            </LaunchHeader>
        </div>
        <div class="mb-2 flex items-center">
            <PageTitle class="" :title="`${question.id ? 'Edit' : 'Create a'} post`">
                <ResetButton
                    header="Reset and start over"
                    message="This will reset your progress and move you back to the start. Are you sure??"
                    @reset="resetForm"
                />
            </PageTitle>
        </div>
        <form @submit.prevent="save">
            <div class="relative" v-auto-animate="{duration: 350}">
                <!-- Content -->
                <div key="question" class="mb-6">
                    <div class="mb-1 flex">
                        <h3 class="font-bold">Post</h3>
                    </div>
                    <div class="mb-4 flex flex-col gap-3">
                        <Select
                            v-if="allowedQuestionTypes.length > 1"
                            name="type"
                            :options="allowedQuestionTypes"
                            v-model="question.type"
                            optionLabel="name"
                            option-value="slug"
                            required
                            checkmark
                            placeholder="Post type"
                            fluid
                        >
                            <template #option="{option}">
                                <div class="flex w-full min-w-0 flex-col gap-1 px-2">
                                    <div class="flex items-center gap-1.5">
                                        <span class="w-6">
                                            <Icon :icon="option.icon" class="" width="20" />
                                        </span>
                                        <span class="text-sm font-semibold">{{ option.name }}</span>
                                    </div>
                                    <span class="w-full min-w-0 max-w-full shrink text-wrap text-xs">
                                        {{ option.description }}
                                    </span>
                                </div>
                            </template>
                        </Select>
                        <Message v-if="question.type === 'gatekeeper'" severity="warn" :closable="false" class="mb-2">
                            Gatekeeper questions control access to subsequent posts. Users must select the correct
                            answer(s) to proceed.
                        </Message>
                        <div class="relative">
                            <Textarea
                                class="w-full"
                                autoResize
                                rows="3"
                                placeholder="What's on your mind?"
                                required
                                v-model="question.title"
                            />
                            <WriteWithAi
                                v-model="question.title"
                                parent="question"
                                :parent-id="question?.id"
                                field="title"
                                class="!absolute bottom-2 right-2 z-10"
                            />
                        </div>
                    </div>
                    <div v-if="hasOptions" class="mb-6">
                        <div class="mb-2 flex items-center justify-between">
                            <h3 class="font-bold">Options</h3>
                            <div class="flex items-center gap-1">
                                <Label for="image_options" class="text-xs">Use images as options</Label>
                                <ToggleSwitch
                                    name="image_options"
                                    input-id="image_options"
                                    v-model="question.params.image_options"
                                />
                            </div>
                        </div>
                        <div class="mb-2 flex flex-col gap-4" v-auto-animate>
                            <template v-for="(option, i) in question.options" :key="i">
                                <div class="mb-3 flex items-start gap-2" v-auto-animate>
                                    <span
                                        class="mt-2 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-askr-green font-bold leading-none text-white"
                                    >
                                        {{ i + 1 }}
                                    </span>
                                    <div
                                        class="flex grow flex-col gap-2 rounded-lg border p-3 dark:border-stone-600"
                                        v-auto-animate
                                    >
                                        <InputText
                                            ref="optionInputRefs"
                                            v-model="option.value"
                                            placeholder="Type here..."
                                            class="w-full"
                                            required
                                            @keydown.enter="addOption"
                                        />
                                        <div v-if="question.type === 'gatekeeper'" class="flex items-center gap-2">
                                            <Checkbox
                                                :inputId="`option_${i}_positive`"
                                                v-model="option.params.gatekeeper_positive"
                                                binary
                                            />
                                            <label
                                                :for="`option_${i}_positive`"
                                                class="text-xs font-semibold text-askr-green-600"
                                            >
                                                Positive Answer (Unlocks next posts)
                                            </label>
                                        </div>
                                        <div class="flex items-center justify-between">
                                            <div v-if="question.type === 'multiple_choice'" class="flex flex-col gap-2">
                                                <div class="flex items-center gap-2">
                                                    <ToggleSwitch
                                                        v-model="option.params.reveal"
                                                        :input-id="`option_${i}_reveal`"
                                                    />
                                                    <label
                                                        :for="`option_${i}_reveal`"
                                                        class="text-xs font-semibold text-askr-green-600"
                                                    >
                                                        Reveal questions
                                                    </label>
                                                </div>
                                            </div>
                                            <div
                                                v-if="$questionModel.is('multiple_choice')"
                                                class="flex items-center gap-2"
                                            >
                                                <label
                                                    :for="`option_${i}_flagged`"
                                                    class="text-xs font-semibold text-askr-green-600"
                                                >
                                                    Flag option (notify admins when selected)
                                                </label>
                                                <Checkbox
                                                    :inputId="`option_${i}_flagged`"
                                                    v-model="option.params.flagged"
                                                    binary
                                                />
                                            </div>
                                        </div>
                                        <div v-if="option.params.reveal" v-auto-animate>
                                            <TagSelect v-model="option.params.reveal_tags" list-class="h-auto" />
                                        </div>
                                        <template v-if="question.params.image_options">
                                            <template v-if="option.id && option.image">
                                                <div class="relative mt-2 flex items-center">
                                                    <img
                                                        :src="option.image.url"
                                                        alt="Option image"
                                                        class="h-autorounded-lg w-full object-cover"
                                                    />
                                                    <Button
                                                        class="!absolute right-2 top-2 h-8 w-8 flex-none bg-red-500 text-white"
                                                        icon="mdi:minus"
                                                        @click="removeOptionImage(i)"
                                                    />
                                                </div>
                                            </template>
                                            <template v-else>
                                                <FileUpload
                                                    class="mt-1"
                                                    :id="option.temp_id"
                                                    ref="optionUpload"
                                                    mode="basic"
                                                    name="files"
                                                    collection="image"
                                                    :parent-id="option.id"
                                                    parent-type="question_option"
                                                    accept="image/*"
                                                />
                                            </template>
                                        </template>
                                    </div>
                                    <Button
                                        class="mt-2 h-8 w-8 flex-none bg-red-500 text-white"
                                        icon="mdi:minus"
                                        @click="removeOption(i)"
                                    />
                                </div>
                            </template>
                        </div>
                        <div class="flex justify-end">
                            <button type="button" class="flex items-center gap-2" @click="addOption">
                                <span class="text-xs font-medium">Add option</span>
                                <span
                                    class="mb-1 flex h-8 w-8 items-center justify-center rounded-full border border-askr-green text-askr-green"
                                >
                                    <Icon icon="mdi:plus" />
                                </span>
                            </button>
                        </div>
                    </div>
                    <div v-auto-animate>
                        <div v-if="showParams" class="mb-4 space-y-4">
                            <div v-if="$user.can('ask detailed questions')">
                                <div class="mb-1 flex">
                                    <h3 class="font-bold">Settings</h3>
                                </div>
                                <div class="mb-2 flex flex-col gap-2" v-auto-animate>
                                    <div class="flex items-center gap-2" v-if="['slider'].includes(question.type)">
                                        <Checkbox
                                            v-model="question.params.slider_range"
                                            inputId="params_slider_range"
                                            name="params[slider_range]"
                                            binary
                                        />
                                        <label for="params_slider_range">Allow range (low - high)</label>
                                    </div>
                                    <div
                                        class="flex items-center gap-2"
                                        v-if="
                                            ['multiple_choice', 'ranking', 'weighting', 'gatekeeper'].includes(
                                                question.type,
                                            )
                                        "
                                    >
                                        <Checkbox
                                            v-model="question.params.randomize_options"
                                            inputId="params_randomize_options"
                                            name="params[randomize_options]"
                                            binary
                                        />
                                        <label for="params_randomize_options">Randomize options order</label>
                                    </div>
                                    <!--                                    <div class="flex items-start gap-2" v-if="['text'].includes(question.type)">-->
                                    <!--                                        <Checkbox-->
                                    <!--                                            v-model="question.params.allow_annotations"-->
                                    <!--                                            inputId="params_allow_annotations"-->
                                    <!--                                            name="params[allow_annotations]"-->
                                    <!--                                            binary-->
                                    <!--                                        />-->
                                    <!--                                        <div class="flex flex-col">-->
                                    <!--                                            <label for="params_allow_annotations">Allow annotations</label>-->
                                    <!--                                            <span class="text-xs">Image and slides stimulus only</span>-->
                                    <!--                                        </div>-->
                                    <!--                                    </div>-->
                                    <div
                                        class="flex items-center gap-2"
                                        v-if="
                                            ['multiple_choice', 'word', 'text', 'annotation', 'gatekeeper'].includes(
                                                question.type,
                                            )
                                        "
                                    >
                                        <Checkbox
                                            v-model="question.params.allow_multiple"
                                            inputId="params_allow_multiple"
                                            name="params[allow_multiple]"
                                            binary
                                        />
                                        <label for="params_allow_multiple">
                                            {{
                                                question.params.matrix
                                                    ? "Allow multiple selections per category"
                                                    : "Allow multiple responses"
                                            }}
                                        </label>
                                    </div>
                                    <div class="flex items-center gap-2" v-if="question.type === 'multiple_choice'">
                                        <Checkbox
                                            v-model="question.params.matrix"
                                            inputId="params_matrix"
                                            name="params[matrix]"
                                            binary
                                        />
                                        <label for="params_matrix">Matrix</label>
                                    </div>
                                    <div
                                        class="flex flex-col gap-2"
                                        v-if="question.type === 'multiple_choice' && question.params.matrix"
                                        v-auto-animate
                                    >
                                        <template v-for="(category, i) in question.categories" :key="i">
                                            <div class="flex items-start gap-2">
                                                <InputText
                                                    v-model="question.categories[i]"
                                                    placeholder="Category name"
                                                    class="w-full"
                                                    required
                                                />
                                                <Button
                                                    class="h-8 w-8 flex-none bg-red-500 text-white"
                                                    icon="mdi:minus"
                                                    @click="removeMatrixCategory(i)"
                                                />
                                            </div>
                                        </template>
                                        <div class="flex justify-end">
                                            <button
                                                type="button"
                                                class="flex items-center gap-2"
                                                @click="addMatrixCategory"
                                            >
                                                <span class="text-xs font-medium">Add category</span>
                                                <span
                                                    class="mb-1 flex h-8 w-8 items-center justify-center rounded-full border border-askr-green text-askr-green"
                                                >
                                                    <Icon icon="mdi:plus" />
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                    <!-- Gatekeeper Settings -->
                                    <div
                                        class="flex items-center gap-2"
                                        v-if="question.type === 'gatekeeper' && question.params.allow_multiple"
                                    >
                                        <Checkbox
                                            v-model="question.params.gatekeeper_all"
                                            inputId="params_gatekeeper_all"
                                            name="params[gatekeeper_all]"
                                            binary
                                        />
                                        <label for="params_gatekeeper_all">User must select ALL positive answers</label>
                                    </div>
                                    <!--                                <div-->
                                    <!--                                    class="flex flex-col gap-2"-->
                                    <!--                                    v-if="question.params.allow_multiple && question.type !== 'post'"-->
                                    <!--                                    v-auto-animate-->
                                    <!--                                >-->
                                    <!--                                    <div class="flex items-center gap-2" v-if="question.params.allow_multiple">-->
                                    <!--                                        <Checkbox-->
                                    <!--                                            v-model="question.params.limit_responses"-->
                                    <!--                                            inputId="params_limit_responses"-->
                                    <!--                                            name="params[limit_responses]"-->
                                    <!--                                            binary-->
                                    <!--                                        />-->
                                    <!--                                        <label for="params_limit_responses">Limit responses</label>-->
                                    <!--                                    </div>-->
                                    <!--                                    <div v-if="question.params.limit_responses">-->
                                    <!--                                        <InputNumber-->
                                    <!--                                            v-model="question.params.response_limit"-->
                                    <!--                                            class="w&#45;&#45;full"-->
                                    <!--                                            showButtons-->
                                    <!--                                            buttonLayout="horizontal"-->
                                    <!--                                            name="params[response_limit]"-->
                                    <!--                                            placeholder="Responses limit"-->
                                    <!--                                            :min="1"-->
                                    <!--                                        />-->
                                    <!--                                    </div>-->
                                    <!--                                </div>-->
                                    <div
                                        class="flex items-center gap-2"
                                        v-if="
                                            [
                                                'multiple_choice',
                                                'ranking',
                                                'slider',
                                                'weighting',
                                                'word',
                                                'gatekeeper',
                                            ].includes(question.type)
                                        "
                                    >
                                        <Checkbox
                                            v-model="question.params.show_charts"
                                            inputId="params_show_charts"
                                            name="params[show_charts]"
                                            binary
                                        />
                                        <label for="params_show_charts">Show results charts</label>
                                    </div>
                                    <div class="flex items-center gap-2" v-if="question.type !== 'post'">
                                        <Checkbox
                                            v-model="question.params.show_responses"
                                            inputId="params_show_responses"
                                            name="params[show_responses]"
                                            binary
                                        />
                                        <label for="params_show_responses">Show others' responses</label>
                                    </div>
                                    <div v-if="question.type === 'text'" class="flex items-center gap-2">
                                        <Checkbox
                                            v-model="question.params.allow_voting"
                                            inputId="params_allow_voting"
                                            name="params[allow_voting]"
                                            binary
                                        />
                                        <label for="params_allow_voting">Allow voting</label>
                                    </div>
                                    <div class="flex items-center gap-2" v-if="question.type !== 'text'">
                                        <Checkbox
                                            v-model="question.params.allow_comments"
                                            inputId="params_allow_comments"
                                            name="params[allow_comments]"
                                            binary
                                        />
                                        <label for="params_allow_comments">Show general comments panel</label>
                                    </div>
                                    <div class="flex items-center gap-2" v-if="question.params.allow_comments">
                                        <Checkbox
                                            v-model="question.params.allow_voice_comments"
                                            inputId="params_allow_voice_comments"
                                            name="params[allow_voice_comments]"
                                            binary
                                        />
                                        <label for="params_allow_voice_comments">Allow voice comments</label>
                                    </div>
                                    <div
                                        class="flex items-center gap-2"
                                        v-if="question.type === 'text' && !question.params.allow_annotations"
                                    >
                                        <Checkbox
                                            v-model="question.params.allow_voice_responses"
                                            inputId="params_allow_voice_responses"
                                            name="params[allow_voice_responses]"
                                            binary
                                        />
                                        <label for="params_allow_voice_responses">Allow voice responses</label>
                                    </div>
                                    <div
                                        class="flex items-center gap-2"
                                        v-if="question.params.allow_comments && question.type !== 'post'"
                                    >
                                        <Checkbox
                                            v-model="question.params.disable_unresponded_comments"
                                            inputId="params_disable_unresponded_comments"
                                            name="params[disable_unresponded_comments]"
                                            binary
                                        />
                                        <label for="params_disable_unresponded_comments">
                                            Disable general comments until question has been responded to
                                        </label>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <Checkbox
                                            v-model="question.is_hidden"
                                            inputId="is_hidden"
                                            name="is_hidden"
                                            binary
                                        />
                                        <label for="is_hidden">Hide question from feed</label>
                                    </div>
                                    <p class="text-xs text-stone-500">
                                        This question appears only after a respondent selects an option that reveals its
                                        tags.
                                    </p>
                                </div>
                            </div>
                            <!-- Stimulus -->
                            <div v-auto-animate>
                                <template v-if="selectedStimulus">
                                    <div>
                                        <h3 class="mb-1 font-bold">Your selected stimulus</h3>
                                        <div class="relative">
                                            <Button
                                                v-if="canReuseStimulus"
                                                :icon="{icon: 'mdi:times', width: 30}"
                                                rounded
                                                class="!absolute -right-2 -top-12 z-10 h-12 w-12 bg-red-600 text-white shadow shadow-stone-600"
                                                @click="question.stimulus_id = null"
                                            />
                                            <div>
                                                <StimulusDisplay :stimulus="selectedStimulus" />
                                                <div class="mt-4">
                                                    <p class="mb-1 text-xs font-bold uppercase">
                                                        {{ selectedStimulus.type.name }}
                                                    </p>
                                                    <h2 class="break-words font-bold">{{ selectedStimulus.title }}</h2>
                                                    <p class="break-words text-sm">
                                                        {{ selectedStimulus.description }}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <template v-else-if="question.stimulus_id === 0">
                                    <div class="relative">
                                        <div class="flex flex-col items-start lg:py-6">
                                            <h3 v-if="!getting" class="mb-1 font-bold">No stimulus selected</h3>
                                            <p class="mb-3 max-w-60 text-center text-xs lg:max-w-none">
                                                You can add various support materials to enhance your post.
                                            </p>
                                            <div class="flex gap-1">
                                                <Button
                                                    v-if="canReuseStimulus"
                                                    :icon="{icon: 'uil:image', width: '18'}"
                                                    rounded
                                                    class="border bg-stone-50 !py-1 !pl-2 !pr-3 text-xs shadow-sm dark:border-stone-700 dark:bg-stone-900"
                                                    @click="question.stimulus_id = null"
                                                >
                                                    Select a stimulus
                                                </Button>
                                                <RouterLink
                                                    :to="{
                                                        name: 'stimuli.create',
                                                        query: {
                                                            ...router.currentRoute.value.query,
                                                            for: 'question',
                                                            group: router.currentRoute.value.query.group,
                                                        },
                                                    }"
                                                >
                                                    <Button
                                                        :icon="{icon: 'mdi:plus', width: '20'}"
                                                        rounded
                                                        class="border bg-stone-50 !py-1 !pl-2 !pr-3 text-xs shadow-sm dark:border-stone-700 dark:bg-stone-900"
                                                    >
                                                        {{ canReuseStimulus ? "Or create one" : "Create a stimulus" }}
                                                    </Button>
                                                </RouterLink>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <div v-else class="relative" key="stimulus">
                                    <div class="mb-2 flex items-start">
                                        <div class="flex flex-col">
                                            <h3 class="font-bold">Stimulus</h3>
                                            <p class="text-xs">
                                                You can add various support materials to enhance your post.
                                            </p>
                                        </div>
                                        <RouterLink
                                            class="ml-auto flex-none"
                                            :to="{
                                                name: 'stimuli.create',
                                                query: {for: 'question', group: router.currentRoute.value.query.group},
                                            }"
                                        >
                                            <Button
                                                :icon="{icon: 'mdi:plus', width: '20'}"
                                                rounded
                                                class="border bg-stone-50 !py-1 !pl-2 !pr-3 text-xs shadow-sm dark:border-stone-700 dark:bg-stone-900"
                                            >
                                                Or create one
                                            </Button>
                                        </RouterLink>
                                    </div>
                                    <div>
                                        <div class="mb-2 flex items-center gap-2">
                                            <InputText
                                                type="search"
                                                placeholder="Start typing to filter stimuli"
                                                fluid
                                                enterkeyhint="enter"
                                                v-model="search.stimuli.search"
                                            />
                                            <Button
                                                :icon="{icon: 'hugeicons:filter', width: 26}"
                                                :rounded="false"
                                                class="aspect-square h-full flex-none !p-2.5"
                                                :class="[
                                                    stimulusFilters.type.length || stimulusFilters.ownership
                                                        ? 'bg-askr-green-500 text-white hover:brightness-110'
                                                        : 'border border-stone-300 bg-stone-50 hover:bg-white dark:border-stone-700 dark:bg-stone-900 dark:hover:bg-stone-800',
                                                ]"
                                                @click="filterStimulusPanel.toggle($event)"
                                            />
                                            <Popover ref="filterStimulusPanel" pt:content:class="px-4 py-3">
                                                <div class="mb-4">
                                                    <h3 class="mb-2 text-sm font-semibold">Ownership</h3>
                                                    <SelectButton
                                                        v-model="stimulusFilters.ownership"
                                                        :options="[
                                                            {label: 'Mine', value: 'mine'},
                                                            {label: 'Others', value: 'others'},
                                                        ]"
                                                        option-label="label"
                                                        option-value="value"
                                                        class="w-full"
                                                    />
                                                </div>
                                                <div>
                                                    <h3 class="mb-2 text-sm font-semibold">Type</h3>
                                                    <div class="flex flex-col gap-2">
                                                        <template v-for="option in StimulusTypes" :key="option.slug">
                                                            <div class="flex items-center gap-2">
                                                                <Checkbox
                                                                    :input-id="`type_${option.slug}`"
                                                                    name="stimulus_type"
                                                                    v-model="stimulusFilters.type"
                                                                    :value="option.slug"
                                                                    :disabled="question.type === 'annotation'"
                                                                />
                                                                <Label
                                                                    :for="`type_${option.slug}`"
                                                                    class="flex items-center gap-1.5"
                                                                    :class="{
                                                                        'cursor-not-allowed opacity-50':
                                                                            question.type === 'annotation',
                                                                    }"
                                                                >
                                                                    <Icon :icon="option.icon" width="18" />
                                                                    <span>{{ option.name }}</span>
                                                                </Label>
                                                            </div>
                                                        </template>
                                                    </div>
                                                </div>
                                            </Popover>
                                        </div>
                                        <StimulusSelect
                                            :stimuli="stimuli"
                                            v-model="question.stimulus_id"
                                            class="mb-3"
                                        />
                                        <div class="mb-8 flex justify-center gap-2">
                                            <button
                                                v-if="question.type !== 'annotation'"
                                                type="button"
                                                @click="question.stimulus_id = 0"
                                                class="rounded-md border bg-stone-50 px-4 py-2 text-xs font-semibold shadow-sm transition hover:bg-stone-100 hover:bg-stone-800 dark:border-stone-700 dark:bg-stone-900"
                                            >
                                                I don't want a stimulus
                                            </button>
                                            <RouterLink :to="{name: 'stimuli.create', query: {for: 'question'}}">
                                                <Button
                                                    :icon="{icon: 'mdi:plus', width: '28'}"
                                                    rounded
                                                    class="rounded-md border bg-stone-50 px-4 py-2 text-xs font-semibold shadow-sm transition hover:bg-stone-100 hover:bg-stone-800 dark:border-stone-700 dark:bg-stone-900"
                                                >
                                                    Create a Stimulus
                                                </Button>
                                            </RouterLink>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <!-- Cover image -->
                            <div class="pb-6">
                                <div class="mb-1 flex flex-col" v-auto-animate>
                                    <h3 class="font-bold">Cover image</h3>
                                    <p class="mb-2 text-xs">
                                        Make your post stand out in the feed with a cover image. You can use the image
                                        from the stimulus above or choose your own.
                                    </p>
                                    <div v-if="question?.params?.cover_image_type !== 'none'">
                                        <h3
                                            class="text-sm font-bold"
                                            v-if="
                                                question?.params?.cover_image_type === 'stimulus' ||
                                                question.cover_image
                                            "
                                        >
                                            {{
                                                question?.params?.cover_image_type === "custom"
                                                    ? "Current Image"
                                                    : "Preview"
                                            }}
                                        </h3>
                                        <CoverImage
                                            class="mb-4"
                                            :question="question"
                                            :stimulus="selectedStimulus"
                                            placement="top"
                                        />
                                    </div>
                                    <div>
                                        <SelectButton
                                            v-model="question.params.cover_image_type"
                                            :options="coverImageOptions"
                                            :allowEmpty="false"
                                            :optionDisabled="disabledCoverImageOptions"
                                            option-label="name"
                                            option-value="value"
                                            data-key="value"
                                            class="w-full"
                                        />
                                    </div>
                                    <div
                                        v-if="question?.params?.cover_image_type === 'custom'"
                                        class="mt-4"
                                        v-auto-animate
                                    >
                                        <div>
                                            <Label for="cover-image" class="mb-1">Search for a stock image</Label>
                                            <StockImageSearch
                                                ref="stockImageSearchEl"
                                                placeholder="Start typing to find royalty free images"
                                                v-model="stockImage"
                                            />
                                        </div>
                                        <div class="flex justify-center" v-if="stockImage">
                                            <Button
                                                class="border border-stone-400 text-xs hover:brightness-110 dark:border-stone-500"
                                                @click="clearStockImage"
                                            >
                                                Upload custom image instead
                                            </Button>
                                        </div>
                                        <div v-else>
                                            <Label for="cover-image" class="mb-1 mt-4">Or, upload your own</Label>
                                            <FileUpload
                                                ref="coverImageUploadRef"
                                                id="image"
                                                name="image"
                                                collection="cover"
                                                :parent-id="question.id"
                                                parent-type="question"
                                                accept="image/*"
                                                :multiple="true"
                                                @select="
                                                    stockImage = null;
                                                    stockImageSearchEl.clearSearch();
                                                "
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <TagSelect v-model="question.tags" />
                        </div>
                    </div>
                    <div class="w-full" v-if="question.type !== 'annotation'">
                        <button type="button" class="flex items-center gap-1 text-xs" @click="showParams = !showParams">
                            <Icon :icon="showParams ? 'akar-icons:chevron-up' : 'teenyicons:cog-outline'" />
                            {{ showParams ? "Hide a" : "A" }}dvanced settings
                        </button>
                    </div>
                </div>
            </div>
            <div class="mb-8 mt-16 flex flex-col items-center">
                <Button
                    type="submit"
                    class="w-full bg-askr-green-500 text-white"
                    :icon="groupId ? 'mingcute:send-plane-fill' : 'mdi:check'"
                    :loading="!!saving"
                >
                    {{ saving || groupId ? "Post" : "Save" }}
                </Button>
            </div>
        </form>
    </div>
</template>
<style scoped></style>
